import { useState, useRef, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import { Box, Stack, Typography, Paper, IconButton, Button } from '@mui/material'
import { keyframes } from '@mui/system'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import EastIcon from '@mui/icons-material/East'
import PlayIcon from '@mui/icons-material/PlayCircleFilledWhiteOutlined'
import PauseIcon from '@mui/icons-material/PauseCircleOutlineOutlined'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { Navigation, Pagination, Autoplay, A11y, Keyboard } from 'swiper/modules'

import { getXrCntsList } from '@/api/vx'

const arrow = keyframes` 
  0%, 60% {
    transform: translate(0, 0);
    opacity: 1;
  }
  20% {
    transform: translate(100%, 0);
    opacity: 1;
  }
  21% {
    opacity: 0;
  }
  50% {
    opacity: 0;
    transform: translate(-100%, 0);
  }
  51% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`

function XRContentPC({ items }) {
  const [progress, setProgress] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)

  const swiperRef = useRef(null)
  const progressRefs = useRef([])

  const BAR_HEIGHT = 180
  const AUTOPLAY_MS = 3000

  useEffect(() => {
    progressRefs.current.forEach((bar) => {
      if (bar) bar.style.transform = 'translateY(100%)'
    })

    const activeBar = progressRefs.current[activeIndex]
    if (!activeBar) return

    let start = performance.now()
    let frame

    const animate = (now) => {
      const elapsed = now - start
      const ratio = Math.min(elapsed / AUTOPLAY_MS, 1)
      activeBar.style.transform = `translateY(${100 - ratio * 100}%)`

      if (ratio < 1) frame = requestAnimationFrame(animate)
    }

    frame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frame)
  }, [activeIndex])

  const handleTextClick = (index) => swiperRef.current?.slideTo(index)

  const togglePlay = () => {
    const swiper = swiperRef.current
    if (!swiper) return

    if (isPlaying) swiper.autoplay.stop()
    else swiper.autoplay.start()

    setIsPlaying(!isPlaying)
  }

  const goToSlide = (index) => {
    swiperRef.current?.slideTo(index)
  }

  return (
    <Stack direction="row" spacing={6} sx={{ position: 'relative' }}>
      <Box sx={{ width: '100%', maxWidth: '624px' }}>
        <Swiper
          slidesPerView={1}
          autoplay={{ delay: AUTOPLAY_MS, disableOnInteraction: false }}
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex)
            setProgress(0)
          }}
          modules={[Autoplay]}
          style={{ width: '100%', aspectRatio: 1.11 / 1 }}
        >
          {items.map((item, index) => (
            <SwiperSlide key={index}>
              <Paper
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: 'none',
                }}
              >
                <Box
                  component="img"
                  src={item.thumbUrl}
                  alt={`${item.title} 콘텐츠 미리보기 이미지`}
                  sx={{
                    width: '100%',
                    aspectRatio: 1.11 / 1,
                    objectFit: 'cover',
                  }}
                />
              </Paper>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Stack flex={1} spacing={1.5}>
        {items.map((item, index) => {
          const isActive = activeIndex === index

          return (
            <Box key={index} sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '2px',
                  height: `${BAR_HEIGHT}px`,
                  backgroundColor: 'grey.100',
                  overflow: 'hidden',
                  transform: 'rotate(180deg)',
                }}
              >
                <Box
                  ref={(el) => (progressRefs.current[index] = el)}
                  sx={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'primary.main',
                    transform: isActive
                      ? `translateY(${100 - progress * 100}%)`
                      : 'translateY(100%)',
                    transition: isActive ? 'transform 0.1s linear' : 'none',
                  }}
                />
              </Box>

              <Stack
                component="button"
                onClick={() => handleTextClick(index)}
                aria-label={`${item.title} 슬라이드 보기`}
                sx={{
                  border: 'none',
                  padding: 0,
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  pl: 4,
                  height: `${BAR_HEIGHT}px`,
                  transition: 'all 0.3s ease',
                  opacity: isActive ? 1 : 0.55,
                  transform: isActive ? 'translateY(0)' : 'translateY(3px)',
                  '&:hover': { opacity: 1 },
                }}
              >
                <Typography component="p" variant="body1" sx={{ mb: 0.5 }}>
                  {item.xr}
                </Typography>

                <Typography
                  component="p"
                  variant="h6"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {item.title}
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    transition: 'all 0.35s ease',
                    opacity: isActive ? 1 : 0,
                    visibility: isActive ? 'visible' : 'hidden',
                    pointerEvents: isActive ? 'auto' : 'none',
                    transform: isActive ? 'translateY(0)' : 'translateY(-12px)',
                    height: isActive ? 'auto' : '0',
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      mb: 4,
                    }}
                  >
                    {item.content}
                  </Typography>

                  <Button
                    component={Link}
                    to={item.detailHref}
                    sx={{
                      p: 0,
                      m: 0,
                      height: 'auto',
                      typography: 'body2',
                      fontWeight: '500',
                      color: 'primary.main',
                    }}
                  >
                    자세히 보기
                    <KeyboardArrowRightIcon sx={{ fontSize: '20px' }} />
                  </Button>
                </Box>
              </Stack>
            </Box>
          )
        })}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          zIndex: 10,
        }}
      >
        <Stack
          direction="row"
          spacing={2.5}
          role="group"
          aria-label="XR 콘텐츠 슬라이드 선택"
        >
          {items.map((_, idx) => (
            <Button
              key={idx}
              onClick={() => goToSlide(idx)}
              aria-label={`콘텐츠 ${idx + 1}번 보기`}
              aria-current={activeIndex === idx ? 'true' : undefined}
              disableRipple
              sx={{
                minWidth: 0,
                p: 0,
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: activeIndex === idx ? 'grey.800' : 'divider',
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: '2px solid #000',
                },
              }}
            />
          ))}
        </Stack>

        <IconButton
          onClick={togglePlay}
          aria-label={isPlaying ? '자동재생 일시정지' : '자동재생 시작'}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
      </Stack>
    </Stack>
  )
}

function XRContentMO({ items }) {
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [current, setCurrent] = useState(1)
  const swiperRef = useRef(null)

  return (
    <Box>
      <Swiper
        modules={[Navigation, Pagination, Autoplay, A11y, Keyboard]}
        spaceBetween={20}
        slidesPerView={1}
        navigation={{
          prevEl: '.xr-button-prev',
          nextEl: '.xr-button-next',
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
          setIsBeginning(swiper.isBeginning)
          setCurrent(swiper.realIndex + 1)
          setIsEnd(swiper.isEnd)
          setTimeout(() => {
            swiper.params.navigation.prevEl = '.xr-button-prev'
            swiper.params.navigation.nextEl = '.xr-button-next'

            swiper.navigation.destroy()
            swiper.navigation.init()
            swiper.navigation.update()
          })
        }}
        onSlideChange={(swiper) => {
          setIsBeginning(swiper.isBeginning)
          setCurrent(swiper.realIndex + 1)
          setIsEnd(swiper.isEnd)
        }}
      >
        {items.map((item, index) => (
          <SwiperSlide key={index}>
            <Paper sx={{ boxShadow: 'none' }}>
              <img
                src={item.thumbUrl}
                alt={item.title}
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  display: 'block',
                }}
              />
              <Box sx={{ mt: 3 }}>
                <Typography>{item.xr}</Typography>
                <Typography variant="subtitle1" sx={{ mt: 1.5 }}>
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                  }}
                >
                  {item.content}
                </Typography>
                <Button
                  component={Link}
                  to={item.detailHref}
                  sx={{
                    p: 0,
                    m: 0,
                    mt: 3,
                    height: 'auto',
                    typography: 'body2',
                    fontWeight: '500',
                    color: 'primary.main',
                  }}
                >
                  자세히 보기
                  <KeyboardArrowRightIcon sx={{ fontSize: '20px' }} />
                </Button>
              </Box>
            </Paper>
          </SwiperSlide>
        ))}
      </Swiper>

      <Stack direction="row" justifyContent="center" spacing={1.5} sx={{ mt: 3 }}>
        <IconButton
          className="xr-button-prev"
          sx={{
            width: '48px',
            height: '48px',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ArrowBackIosNewIcon
            sx={{
              fontSize: '20px',
              color: isBeginning ? 'grey.500' : 'text.primary',
            }}
          />
        </IconButton>
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ px: 2 }}>
          <Typography fontWeight={700}>{current}</Typography>
          <Box sx={{ width: '1px', height: '12px', bgcolor: 'divider' }} />
          <Typography>{items.length}</Typography>
        </Stack>
        <IconButton
          className="xr-button-next"
          sx={{
            width: '48px',
            height: '48px',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            cursor: 'pointer',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <ArrowForwardIosIcon
            sx={{
              fontSize: '20px',
              color: isEnd ? 'grey.500' : 'text.primary',
            }}
          />
        </IconButton>
      </Stack>
    </Box>
  )
}

export default function XRContentSection() {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const arrowAnimation = useMemo(() => `${arrow} 0.5s ease-in-out`, [])

  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(true)

  // 시연용 XR 썸네일 5개 고정 (파일서버 미완일 때 임시로 사용)
  const DEMO_THUMBS = useMemo(
    () => [
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B004/B004_1.png',
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A003/A003_1.png',
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B005/B005_1.png',
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/B003/B003_1.png',
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A001/A001_1.png',
      'https://knou.gcdn.gov-ntruss.com/knou-test/vlab/contents/A002/A002_1.png',
    ],
    []
  )

  useEffect(() => {
    let mounted = true

    const fetchList = async () => {
      try {
        setLoading(true)

        const body = {
          page: 1,
          pageSize: 3,
          recordSize: 3,
          grandDivCode: '',
          midiumDivCode: '',
          smallDivCode: '',
          searchNm: '',
        }

        const res = await getXrCntsList(body)
        const pageData = res?.data
        const list = pageData?.content ?? []

        const mapped = list.slice(0, 3).map((row, index) => ({
          id: row.eduLectureId ?? index,
          cmsContentsId: row.cmsContentsId ?? '',
          title: row.cmsContentsName ?? '',
          xr: index % 2 === 0 ? '실습형XR' : '체험형XR',
          Cat: row.grandDivCode ?? '',
          subCat: row.midiumDivCode ?? '',
          detailCat: row.smallDivCode ?? '',

          // ===========================
          // 기존 로직(파일서버 연결 후 사용 예정)
          // thumbUrl:
          //   row.thumbnailId && String(row.thumbnailId).trim() !== ''
          //     ? row.thumbnailId
          //     : '/assets/images/No image.png',
          //
          // 시연용 로직(오늘만 임시): picsum 5개 고정
          thumbUrl: DEMO_THUMBS[index] ?? '/assets/images/No image.png',
          // ===========================

          content: row.cmsSubstatnceBody ?? '',
          detailHref: buildLangHref(`/contents/xrview/${row.cmsContentsId ?? ''}`, lang),
        }))

        if (mounted) setAllItems(mapped)
      } catch (e) {
        if (mounted) setAllItems([])
      } finally {
        if (mounted) setLoading(false)
      }
    }

    fetchList()

    return () => {
      mounted = false
    }
  }, [lang, DEMO_THUMBS])

  return (
    <Box
      sx={{
        mx: 'auto',
        py: isMobile ? 10 : 15,
        width: '100%',
        maxWidth: '1440px',
        minHeight: isMobile ? 'initial' : 'calc(100vh - 120px)',
        px: 2,
      }}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'flex-end'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        spacing={2}
        sx={{ maxWidth: '1400px', mx: 'auto', mb: 7 }}
      >
        <Box>
          <Typography
            component="h3"
            variant={isMobile ? 'body1' : 'subtitle3'}
            fontWeight={700}
            sx={{ color: 'primary.main', mb: 2 }}
          >
            XR콘텐츠
          </Typography>
          <Typography component="p" fontWeight={600} variant={isMobile ? 'h6' : 'h4'}>
            위험하거나 구현이 어려운 실습,{isMobile ? <span>&nbsp;</span> : <br />}
            이제 가상현실에서{isMobile ? <br /> : <span>&nbsp;</span>}직접 경험해 주세요.
          </Typography>
        </Box>
        <Stack
          component={Link}
          to={buildLangHref('/contents/xr', lang)}
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            pb: 1.5,
            textDecoration: 'none',
            color: 'inherit',
            display: 'inline-flex',
            '&:hover .arrow-icon': {
              animation: arrowAnimation,
            },
          }}
        >
          <Typography fontWeight={600}>더 많은 콘텐츠 보기</Typography>

          <IconButton
            aria-hidden="true"
            sx={{
              width: isMobile ? '32px' : '40px',
              height: isMobile ? '32px' : '40px',
              border: '1px solid',
              borderColor: 'grey.dark',
              color: 'grey.dark',
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <EastIcon sx={{ fontSize: isMobile ? '16px' : '20px' }} />
          </IconButton>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ py: 6 }}>
          <Typography color="text.secondary">콘텐츠를 불러오는 중입니다...</Typography>
        </Box>
      ) : (
        <>
          {isMobile ? <XRContentMO items={allItems} /> : <XRContentPC items={allItems} />}
        </>
      )}
    </Box>
  )
}
